import { environment } from 'src/environments/environment';
import {
  resolveUserMessage,
  UserMessage
} from './order-user-messages';

export type GraphQLErrorLike = {
  message: string;
  extensions?: {
    code?: string;
    userMessage?: string;
    statusCode?: number;
    details?: Record<string, unknown>;
  };
};

export interface GraphqlClientError extends Error {
  graphQLErrors?: GraphQLErrorLike[];
}

export function extractGraphQLErrors(error: unknown): GraphQLErrorLike[] {
  const err = error as {
    graphQLErrors?: GraphQLErrorLike[];
    error?: { errors?: GraphQLErrorLike[] };
    errors?: GraphQLErrorLike[];
  };

  if (Array.isArray(err.graphQLErrors) && err.graphQLErrors.length > 0) {
    return err.graphQLErrors;
  }

  if (Array.isArray(err.error?.errors) && err.error!.errors!.length > 0) {
    return err.error!.errors!;
  }

  if (Array.isArray(err.errors) && err.errors.length > 0) {
    return err.errors;
  }

  return [];
}

export function createGraphqlClientError(
  errors: GraphQLErrorLike[],
  fallbackMessage = 'Error en la solicitud GraphQL.'
): GraphqlClientError {
  const err = new Error(errors[0]?.message ?? fallbackMessage) as GraphqlClientError;
  err.graphQLErrors = errors;
  return err;
}

export function getGraphqlErrorCode(error: unknown): string | null {
  const gqlError = extractGraphQLErrors(error)[0];
  return gqlError?.extensions?.code ?? null;
}

export function getGraphqlErrorDetails(
  error: unknown
): Record<string, unknown> | null {
  const gqlError = extractGraphQLErrors(error)[0];
  return gqlError?.extensions?.details ?? null;
}

export function getGraphqlErrorUserMessage(error: unknown): string | null {
  const gqlError = extractGraphQLErrors(error)[0];
  const fromExtensions = gqlError?.extensions?.userMessage?.trim();
  return fromExtensions || null;
}

export function resolveOrderGraphqlUserMessage(
  error: unknown,
  fallback = 'Ocurrió un error inesperado. Contacta a Soporte TI.'
): UserMessage {
  const code = getGraphqlErrorCode(error);
  const details = getGraphqlErrorDetails(error);
  const message = resolveUserMessage(
    code,
    [],
    fallback,
    getGraphqlErrorUserMessage(error)
  );

  if (code === 'ORDER_NOT_EDITABLE' && details?.['orderStatus']) {
    return {
      ...message,
      body: `${message.body} Estado actual: ${String(details['orderStatus'])}.`,
    };
  }

  return message;
}

/** Errores que indican grilla desfasada → el padre debe recargar del backend. */
export function shouldRefreshAfterOrderError(error: unknown): boolean {
  return resolveOrderGraphqlUserMessage(error).action === 'refresh';
}

/** Solo para logs o pantallas en modo desarrollo. */
export function extractGraphQLErrorMessage(
  error: unknown,
  fallback = 'Ocurrió un error inesperado.'
): string {
  if (!environment.production) {
    const gqlError = extractGraphQLErrors(error)[0];
    if (gqlError?.message) {
      return gqlError.message;
    }
  }

  if (!error) {
    return fallback;
  }

  if (error instanceof Error && error.message) {
    return environment.production ? fallback : error.message;
  }

  return fallback;
}

export function logOrderDevDetail(context: string, detail: unknown): void {
  if (!environment.production && detail != null) {
    console.warn(`[Order:${context}]`, detail);
  }
}

export function toGraphqlClientError(error: unknown): GraphqlClientError {
  const existing = extractGraphQLErrors(error);
  if (existing.length > 0) {
    if ((error as GraphqlClientError).graphQLErrors?.length) {
      return error as GraphqlClientError;
    }
    return createGraphqlClientError(existing);
  }

  const err = new Error(
    error instanceof Error ? error.message : 'Error en la solicitud GraphQL.'
  ) as GraphqlClientError;
  err.graphQLErrors = [];
  return err;
}
